// Custom Pages helpers for a storefront. Custom Pages are additional homepages served at
// {domain}/{slug}; each has its own banners and sections (the shopper filters both by the page's
// GUID). Pages are a first-class API resource (see store/endpoints/StorefrontPageEndpoints.ts); the
// helpers here handle slug validation, ordering, and the page-scoping of the working section/banner
// lists.
//
// Sections/banners with no `page` (or an empty one) belong to the main homepage.

import { IStorefrontBanner, IStorefrontPage, IStorefrontSection } from '../../../models/Storefront';

// Hero banner presentation fields shared by the main homepage (homePageConfig) and custom pages
// (their IStorefrontPage rows). Both carry the same field names, so either object can back the
// banner editor panel's hero controls. All strictly opt-in — absent renders the page-width banner
// exactly as before.
export interface IHeroBannerOptions {
    bannerFullWidth?: boolean;
    bannerImageFit?: 'fit' | 'fill';
    // Floats the transparent nav header over the hero. Requires bannerFullWidth (desktop only live).
    bannerUnderHeader?: boolean;
    // CSS color tinting the floating header bar before scroll (e.g. rgba(0,0,0,0.35)).
    bannerUnderHeaderBackground?: string;
    // CSS box-shadow under the floating header bar before scroll.
    bannerUnderHeaderShadow?: string;
}

// Reserved slugs that would collide with the shopper app's own routes. A custom page may not use
// any of these (case-insensitive). Mirrors the shopper site's fixed route table plus 'pages'.
export const RESERVED_SLUGS: string[] = [
    'home', 'search', 'products', 'dynamicProducts', 'bundles', 'cart', 'carts', 'checkout',
    'checkoutPrerequisites', 'account', 'order-processing', 'order-complete', 'prepared-cart',
    'login', 'token', 'signup', 'api', 'public', 'themes', 'pages',
];

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const RESERVED_LOWER = new Set(RESERVED_SLUGS.map((s) => s.toLowerCase()));

// Client-side GUID generation, matching the precedent in NavPods/StorefrontNavPodsEditor (genId).
export const genPageId = (): string =>
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `page-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Custom pages in display order (by sortOrder, then title/slug for stability).
export function sortedPages(pages: IStorefrontPage[]): IStorefrontPage[] {
    return [...(pages ?? [])].sort((a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        || (a.title ?? a.slug).localeCompare(b.title ?? b.slug));
}

// Turn a free-text title into a candidate slug (lowercase, hyphen-separated, alphanumerics only).
export function slugify(input: string): string {
    return (input ?? '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 50);
}

// Validate a slug against the format, length, reserved list, and uniqueness among the other pages.
// `existingPages` should be the full page list; the page being edited is matched out by `selfId`
// so renaming a page to its own slug is allowed. Returns an error string, or null when valid.
// The API is authoritative (it re-validates the same rules server-side); this is for instant feedback.
export function validateSlug(slug: string, existingPages: IStorefrontPage[], selfId?: string): string | null {
    const value = (slug ?? '').trim();
    if (!value) return 'A slug is required.';
    if (value.length > 50) return 'Slug must be 50 characters or fewer.';
    if (value !== value.toLowerCase()) return 'Slug must be lowercase.';
    if (!SLUG_PATTERN.test(value)) return 'Use lowercase letters, numbers, and single hyphens (e.g. "about-us").';
    if (RESERVED_LOWER.has(value.toLowerCase())) return `"${value}" is a reserved path and can't be used.`;
    const clash = existingPages.some((p) => p.id !== selfId && (p.slug ?? '').toLowerCase() === value.toLowerCase());
    if (clash) return 'Another page already uses this slug.';
    return null;
}

// --- Page-scoping helpers for the working section/banner lists ------------------------------
// The batch save endpoints replace the FULL list, so the editors must always keep and submit every
// page's items and filter only what's DISPLAYED. These helpers do the filtering by page.

// True when an item (its `page` field) belongs to the given editing context. `pageId` undefined
// (or empty) = the main homepage, which owns items with no/empty page.
export function isOnPage(itemPage: string | undefined, pageId?: string): boolean {
    if (!pageId) return !itemPage;
    return itemPage === pageId;
}

export const sectionsForPage = (sections: IStorefrontSection[], pageId?: string): IStorefrontSection[] =>
    sections.filter((s) => isOnPage(s.page, pageId));

export const bannersForPage = (banners: IStorefrontBanner[], pageId?: string): IStorefrontBanner[] =>
    banners.filter((b) => isOnPage(b.page, pageId));

// Reintegrate a reordered subset of a page's items back into the full list, preserving the
// positions of every OTHER page's items. `reorderedSubset` must be a permutation of the items in
// `full` for which `predicate` is true. Used so reordering within one page doesn't disturb — or
// drop — the hidden pages' items in the full list that gets batch-saved.
export function mergeReorderedSubset<T>(full: T[], predicate: (t: T) => boolean, reorderedSubset: T[]): T[] {
    let i = 0;
    return full.map((item) => (predicate(item) ? reorderedSubset[i++] : item));
}
